import { useQuery } from "@tanstack/react-query";
import { tournamentApi } from "@bw/shared/api";
import { useAuth } from "@bw/shared/hooks";
import type { TournamentPlayerEntryPageResponse } from "@bw/shared/types/generated";
import { queryKeys } from "../../../common/query/queryClient";

/**
 * The caller's draw entries across EVERY tournament (GET /users/me/registrations
 * → TournamentPlayerEntryPageResponse). AUTH-gated and SERVER-SCOPED to the token
 * user — no account or guardian identifiers
 * is threaded through; the route resolves the caller from the token. The call
 * fires ONLY when there is a logged-in user (mirrors useMyRegistration), so an
 * anonymous viewer never triggers it.
 *
 * The list is SERVER-ORDERED (newest-first); the consuming page renders it
 * verbatim and never re-sorts. This is the paginated cross-tournament variant of
 * useMyRegistration (the per-tournament read), backing the "My entries" history.
 */
export function useMyRegistrations(page = 1, entryId?: string, enabled = true) {
  const { user } = useAuth();
  return useQuery<TournamentPlayerEntryPageResponse>({
    queryKey: [...queryKeys.tournaments.myRegistrations(), user?.uid, { page, entryId }],
    queryFn: ({ signal }) =>
      tournamentApi.getMyRegistrations({ limit: 20, offset: entryId ? 0 : (page - 1) * 20, entry_id: entryId }, signal),
    enabled: Boolean(user) && enabled,
    staleTime: 0,
    refetchOnMount: "always",
  });
}
